// Catalog backing the agent creation wizard.
//
// The wizard is deliberately outcome-shaped ("what may this agent do") rather
// than engine-shaped ("what temperature"). Each capability below is a real
// AgentTool that gets upserted against the agent after it is created — the
// toggles are not cosmetic.
import { AgentTool } from 'api/services/aiAgents.service';

export interface Capability {
  id: string;
  label: string;
  description: string;
  /** Tool written to the agent when this capability is on. */
  tool: Omit<AgentTool, 'id' | 'agent_id'>;
  /** True when the tool needs an endpoint wired up in the editor before it works. */
  needsWiring?: boolean;
  defaultOn?: boolean;
}

const obj = (properties: Record<string, any>, required: string[] = []) => ({
  type: 'object',
  properties,
  required,
});

export const CAPABILITIES: Capability[] = [
  {
    id: 'answer_questions',
    label: 'Answer questions',
    description: 'Hours, prices, services, directions — answered from the knowledge you gave it.',
    defaultOn: true,
    tool: {
      name: 'answer_questions',
      description: 'Answer a caller question using the business knowledge base.',
      execution_type: 'knowledge_base',
      enabled: true,
      sort_order: 1,
      parameters: obj({ question: { type: 'string', description: 'The caller question' } }, ['question']),
      execution_config: {},
    },
  },
  {
    id: 'book_appointment',
    label: 'Book appointment',
    description: 'Check availability and confirm a slot.',
    needsWiring: true,
    tool: {
      name: 'book_appointment',
      description: 'Check availability and book an appointment for the caller.',
      execution_type: 'api',
      enabled: true,
      sort_order: 2,
      parameters: obj({
        service: { type: 'string', description: 'Service requested' },
        preferred_time: { type: 'string', description: 'Preferred date/time in ISO 8601' },
        customer_name: { type: 'string', description: 'Name to book under' },
      }, ['service', 'preferred_time']),
      execution_config: {},
    },
  },
  {
    id: 'reschedule_booking',
    label: 'Reschedule',
    description: 'Move an existing booking to a new slot.',
    needsWiring: true,
    tool: {
      name: 'reschedule_booking',
      description: 'Move an existing booking to a different time.',
      execution_type: 'api',
      enabled: true,
      sort_order: 3,
      parameters: obj({
        booking_reference: { type: 'string', description: 'Existing booking reference' },
        new_time: { type: 'string', description: 'New date/time in ISO 8601' },
      }, ['booking_reference', 'new_time']),
      execution_config: {},
    },
  },
  {
    id: 'cancel_booking',
    label: 'Cancel booking',
    description: 'Cancel, explaining the policy.',
    needsWiring: true,
    tool: {
      name: 'cancel_booking',
      description: 'Cancel an existing booking and explain the cancellation policy.',
      execution_type: 'api',
      enabled: true,
      sort_order: 4,
      parameters: obj({
        booking_reference: { type: 'string', description: 'Existing booking reference' },
        reason: { type: 'string', description: 'Reason given by the caller' },
      }, ['booking_reference']),
      execution_config: {},
    },
  },
  {
    id: 'send_whatsapp_link',
    label: 'Send WhatsApp link',
    description: 'Price list, location or payment link over WhatsApp.',
    needsWiring: true,
    tool: {
      name: 'send_whatsapp_link',
      description: 'Send the caller a WhatsApp message containing a link.',
      execution_type: 'api',
      enabled: true,
      sort_order: 5,
      parameters: obj({
        phone: { type: 'string', description: 'Destination in E.164' },
        link_type: { type: 'string', enum: ['price_list', 'location', 'payment'], description: 'Which link to send' },
      }, ['phone', 'link_type']),
      execution_config: {},
    },
  },
  {
    id: 'capture_lead',
    label: 'Capture a lead',
    description: "Take the caller's details and file them in your CRM.",
    defaultOn: false,
    tool: {
      name: 'capture_lead',
      description: "Record the caller as a lead with their name, phone and what they want.",
      execution_type: 'api',
      enabled: true,
      sort_order: 6,
      parameters: obj({
        name: { type: 'string', description: 'Caller name, if they gave one' },
        phone: { type: 'string', description: 'E.164 — a lead cannot exist without it' },
        email: { type: 'string', description: 'Email, if offered' },
        notes: { type: 'string', description: 'What they are interested in' },
      }, ['phone']),
      execution_config: {},
    },
  },
  {
    id: 'log_call',
    label: 'Log the call to the CRM',
    description: 'Files the finished call against the caller, so follow-up is not blind.',
    defaultOn: false,
    tool: {
      name: 'log_call',
      description: 'Record the finished call against the caller in the CRM.',
      execution_type: 'api',
      enabled: true,
      sort_order: 7,
      parameters: obj({
        phone: { type: 'string', description: 'E.164 of the caller' },
        subject: { type: 'string', description: 'One line: what the call was about' },
        direction: { type: 'string', enum: ['Inbound', 'Outbound'], description: 'Which way the call went' },
        duration_seconds: { type: 'integer', description: 'How long the call lasted' },
        outcome: { type: 'string', description: 'How it ended' },
      }, ['phone']),
      execution_config: {},
    },
  },
  {
    id: 'take_message',
    label: 'Take a message',
    description: 'Record a note for the team to follow up.',
    defaultOn: true,
    tool: {
      name: 'take_message',
      description: 'Record a message from the caller for the team.',
      execution_type: 'static',
      enabled: true,
      sort_order: 8,
      parameters: obj({
        caller_name: { type: 'string', description: 'Who is calling' },
        message: { type: 'string', description: 'The message to pass on' },
      }, ['message']),
      execution_config: {},
    },
  },
  {
    id: 'escalate_to_human',
    label: 'Escalate to human',
    description: 'Hand the call to a person when it goes beyond the agent.',
    defaultOn: true,
    tool: {
      name: 'escalate_to_human',
      description: 'Transfer the conversation to a human agent.',
      execution_type: 'static',
      enabled: true,
      sort_order: 9,
      parameters: obj({ reason: { type: 'string', description: 'Why the escalation is needed' } }),
      execution_config: {},
    },
  },
];

export interface AgentTemplate {
  id: string;
  label: string;
  blurb: string;
  /** Capability ids switched on when this template is picked. */
  capabilities: string[];
  greeting: string;
  systemPrompt: string;
  knowledgePlaceholder: string;
}

export const TEMPLATES: AgentTemplate[] = [
  {
    id: 'salon_receptionist',
    label: 'Salon receptionist',
    blurb: 'Answers pricing and availability, books and reschedules appointments.',
    capabilities: ['answer_questions', 'book_appointment', 'reschedule_booking', 'cancel_booking', 'send_whatsapp_link', 'escalate_to_human'],
    greeting: 'Hi, thanks for calling! How can I help you today?',
    systemPrompt:
      'You are the receptionist for a salon. Answer questions about services, prices and timings, and book appointments. Be warm and concise. If a caller asks about bridal or event packages, escalate to a human rather than quoting a price.',
    knowledgePlaceholder: 'Services and prices, stylist names, opening hours, cancellation policy, how to find the salon.',
  },
  {
    id: 'clinic_receptionist',
    label: 'Clinic receptionist',
    blurb: 'Handles appointment booking and routine patient questions.',
    capabilities: ['answer_questions', 'book_appointment', 'reschedule_booking', 'cancel_booking', 'take_message', 'escalate_to_human'],
    greeting: 'Hello, thank you for calling. How may I help you?',
    systemPrompt:
      'You are the receptionist for a clinic. Book and reschedule appointments and answer questions about consultation timings, fees and location. Never give medical advice — take a message or escalate to a human instead.',
    knowledgePlaceholder: 'Doctors and their timings, consultation fees, address and directions, insurance accepted.',
  },
  {
    id: 'lead_qualifier',
    label: 'Lead qualifier',
    blurb: 'Qualifies inbound interest and routes the good ones to sales.',
    capabilities: ['answer_questions', 'capture_lead', 'take_message', 'send_whatsapp_link', 'escalate_to_human'],
    greeting: 'Hi! Thanks for your interest — can I ask a couple of quick questions?',
    systemPrompt:
      'You qualify inbound leads. Find out what the caller needs, their timeline and budget, then either send details over WhatsApp or hand a qualified lead to a human. Keep it to a few questions — do not interrogate.',
    knowledgePlaceholder: 'Products or plans, pricing tiers, who the ideal customer is, what disqualifies a lead.',
  },
  {
    id: 'win_back',
    label: 'Win-back caller',
    blurb: 'Outbound — reaches lapsed customers with an offer.',
    capabilities: ['answer_questions', 'book_appointment', 'send_whatsapp_link', 'escalate_to_human'],
    greeting: 'Hi, this is a quick courtesy call — is now a good time?',
    systemPrompt:
      'You call customers who have not returned in a while. Be brief and respectful, check whether it is a good time, mention the current offer, and book them in if they are interested. Accept a no gracefully and never call back after a refusal.',
    knowledgePlaceholder: 'The current offer and its expiry, what these customers previously bought, objection handling.',
  },
  {
    id: 'feedback_caller',
    label: 'Feedback caller',
    blurb: 'Outbound — collects a short satisfaction rating after a visit.',
    capabilities: ['answer_questions', 'take_message', 'escalate_to_human'],
    greeting: 'Hi! Do you have a minute to share how your recent visit went?',
    systemPrompt:
      'You collect short feedback after a visit. Ask for a rating out of five and one sentence of detail. If the rating is low, apologise, capture the specifics and escalate to a human. Keep the whole call under a minute.',
    knowledgePlaceholder: 'What service the customer received, who served them, what a good outcome looks like.',
  },
  {
    id: 'blank',
    label: 'Start blank',
    blurb: 'No presets — configure everything yourself.',
    capabilities: ['answer_questions'],
    greeting: 'Hello! How can I assist you today?',
    systemPrompt: '',
    knowledgePlaceholder: 'Anything the agent should know: services, prices, hours, policies, directions.',
  },
];

export const TONES = ['Warm', 'Professional', 'Energetic', 'Calm'] as const;
export type Tone = typeof TONES[number];

/** Appended to the system prompt so tone is a real instruction, not decoration. */
export const toneInstruction = (tone: Tone): string => {
  const m: Record<Tone, string> = {
    Warm: 'Speak warmly and personally, as a friendly neighbour would.',
    Professional: 'Speak crisply and professionally. Avoid filler and small talk.',
    Energetic: 'Speak with energy and enthusiasm, while staying easy to follow.',
    Calm: 'Speak calmly and unhurriedly, especially when the caller sounds stressed.',
  };
  return m[tone];
};

/** Display voices. `id` is what goes to the runtime as the voice name. */
export const VOICES = [
  { id: 'aaravi', name: 'Aaravi', gender: 'Female', style: 'Warm' },
  { id: 'dev', name: 'Dev', gender: 'Male', style: 'Friendly' },
  { id: 'mira', name: 'Mira', gender: 'Female', style: 'Professional' },
  { id: 'arnav', name: 'Arnav', gender: 'Male', style: 'Calm' },
  { id: 'isha', name: 'Isha', gender: 'Female', style: 'Energetic' },
  { id: 'rehan', name: 'Rehan', gender: 'Male', style: 'Confident' },
];

/**
 * Realtime voices are the MODEL's own, not a TTS provider's.
 * voicex's GeminiRealtimeSession hands llm_config.voice_name straight to Gemini
 * Live, which knows only this set (xtreme-ai-agent docs/GEMINI_REALTIME.md) — a
 * pipeline voice id like 'aaravi' is not a voice it can speak with.
 */
export const REALTIME_VOICES = [
  { id: 'Aoede', name: 'Aoede', gender: 'Female', style: 'Warm' },
  { id: 'Kore', name: 'Kore', gender: 'Female', style: 'Professional' },
  { id: 'Despina', name: 'Despina', gender: 'Female', style: 'Bright' },
  { id: 'Puck', name: 'Puck', gender: 'Male', style: 'Energetic' },
  { id: 'Charon', name: 'Charon', gender: 'Male', style: 'Calm' },
  { id: 'Fenrir', name: 'Fenrir', gender: 'Male', style: 'Confident' },
];

/** The voices that mean anything in this mode. */
export const voicesFor = (realtime: boolean) => (realtime ? REALTIME_VOICES : VOICES);

/** BCP-47 codes so the value is what the runtime actually expects. */
export const LANGUAGES = [
  { code: 'en-IN', label: 'English (India)' },
  { code: 'en-US', label: 'English (US)' },
  { code: 'en-GB', label: 'English (UK)' },
  { code: 'hi-IN', label: 'Hindi' },
  { code: 'pa-IN', label: 'Punjabi' },
  { code: 'ta-IN', label: 'Tamil' },
  { code: 'te-IN', label: 'Telugu' },
  { code: 'bn-IN', label: 'Bengali' },
  { code: 'mr-IN', label: 'Marathi' },
  { code: 'kn-IN', label: 'Kannada' },
  { code: 'ml-IN', label: 'Malayalam' },
  { code: 'gu-IN', label: 'Gujarati' },
];
